// Ayuda de un paso del wizard: botón «?» junto al título que abre «¿Cómo funciona?» con las zonas
// de la pantalla, cómo armar la venta en 4 pasos y una leyenda. El contenido llega por props.
import { useState, type ReactNode } from 'react';
import {
    ArrowRight,
    CircleCheck,
    CircleHelp,
    CreditCard,
    Gift,
    MousePointerClick,
    Package,
    Plus,
    Smartphone,
    Sparkles,
    Tag,
    X,
    Zap,
    type LucideIcon,
} from 'lucide-react';
import { Button, Modal } from '@/kit';
import { cn } from '@/lib/cn';

export const HOW_IT_WORKS_TITLE = '¿Cómo funciona?';

const SECTION = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

export interface HowItWorksContent {
    /** Esquema de la pantalla con los colores de cada zona. */
    sketch: ReactNode;
    zones: readonly { name: string; text: string; swatch: string }[];
    /** Título de los pasos; sin él, «Cómo armar la venta». */
    stepsTitle?: string;
    steps: readonly { icon: LucideIcon; title: string; text: string }[];
    /** Título de la leyenda («Colores de las promos», «Elementos»). */
    legendTitle: string;
    legend: readonly { icon: LucideIcon; color: string; name: string; text: string }[];
}

/** Esquema de «Elige el teléfono»: banner arriba, tarjetas a la izquierda y detalle a la derecha. */
function PhoneScreenSketch() {
    return (
        <div
            aria-hidden
            className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3"
        >
            <div className="h-5 rounded border border-info bg-info-soft" />
            <div className="grid grid-cols-[2fr_1fr] gap-2">
                <div className="grid grid-cols-3 gap-1.5">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div
                            key={i}
                            className="h-8 rounded border border-success bg-success-soft"
                        />
                    ))}
                </div>
                <div className="rounded border border-promo bg-promo-soft" />
            </div>
        </div>
    );
}

/** Contenido de «Elige el teléfono» (paso 04); es el que se ve sin props. */
export const PHONE_SELECT_HOW_IT_WORKS: HowItWorksContent = {
    sketch: <PhoneScreenSketch />,
    zones: [
        {
            name: 'Banner de arriba',
            text: 'las promos de toda la tienda: venta, referidos y crédito.',
            swatch: 'border-info bg-info-soft',
        },
        {
            name: 'Tarjetas',
            text: 'las promos propias de cada teléfono.',
            swatch: 'border-success bg-success-soft',
        },
        {
            name: 'Panel de detalle',
            text: 'todo lo del teléfono elegido y el precio final.',
            swatch: 'border-promo bg-promo-soft',
        },
    ],
    steps: [
        {
            icon: MousePointerClick,
            title: 'Toca un teléfono',
            text: 'Se abre su detalle a la derecha.',
        },
        {
            icon: Sparkles,
            title: 'Elige promos',
            text: 'Opcional; el precio se actualiza al momento.',
        },
        { icon: Tag, title: 'Código del cliente', text: 'Opcional, en el pie del panel.' },
        {
            icon: ArrowRight,
            title: 'Continuar',
            text: 'Aparta el equipo 30 min y pasa al siguiente paso.',
        },
    ],
    legendTitle: 'Colores de las promos',
    legend: [
        {
            icon: Zap,
            color: 'text-warning',
            name: 'Amarillo',
            text: 'bono a futuro (Wallet, tiempo aire)',
        },
        { icon: Gift, color: 'text-info', name: 'Azul', text: 'regalo (accesorio gratis)' },
        { icon: CreditCard, color: 'text-promo', name: 'Morado', text: 'promo de crédito' },
        { icon: Sparkles, color: 'text-success', name: 'Verde', text: 'descuento al teléfono' },
    ],
};

const SKETCH_BAND = 'rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.06em]';

/** Esquema de «Accesorios»: catálogo a la izquierda y resumen con «Continuar» a la derecha. */
function BuildSaleScreenSketch() {
    return (
        <div aria-hidden className="grid grid-cols-[2fr_1fr] gap-2">
            <div className="flex flex-col gap-1 rounded-lg border-2 border-success bg-card p-2">
                <span className={cn(SKETCH_BAND, 'text-muted-foreground')}>Cambia por un kit</span>
                <span className={cn(SKETCH_BAND, 'bg-info-soft text-info')}>
                    Regalos (si aplica)
                </span>
                <span className={cn(SKETCH_BAND, 'text-muted-foreground')}>
                    Accesorios · SIM · Módems · Servicios
                </span>
            </div>
            <div className="flex flex-col gap-1 rounded-lg border-2 border-promo bg-card p-2">
                <span className="text-[10px] font-semibold text-promo">Resumen</span>
                <span className="h-1.5 rounded-full bg-promo-soft" />
                <span className="h-1.5 rounded-full bg-promo-soft" />
                <span className="mt-auto rounded bg-foreground py-0.5 text-center text-[10px] font-semibold text-background">
                    Continuar
                </span>
            </div>
        </div>
    );
}

/** Contenido de «Accesorios» (paso 05); `next` es el paso al que lleva «Continuar». */
export function buildSaleHowItWorks(next = 'Pago'): HowItWorksContent {
    return {
        sketch: <BuildSaleScreenSketch />,
        zones: [
            {
                name: 'Catálogo',
                text: 'elige qué más va en la venta.',
                swatch: 'border-success bg-success-soft',
            },
            {
                name: 'Resumen',
                text: 'lo que vas a cobrar y «Continuar».',
                swatch: 'border-promo bg-promo-soft',
            },
        ],
        steps: [
            { icon: Smartphone, title: 'Revisa el equipo', text: 'Opcional: cambia por kit.' },
            { icon: Gift, title: 'Elige el regalo', text: 'Si la promo lo pide.' },
            {
                icon: Plus,
                title: 'Agrega accesorios',
                text: 'Cables, SIM, módems, servicios.',
            },
            { icon: ArrowRight, title: 'Continuar', text: `Pasa a ${next}.` },
        ],
        legendTitle: 'Elementos',
        legend: [
            {
                icon: CircleCheck,
                color: 'text-success',
                name: 'Crédito / Contado',
                text: 'verde = financiable; gris = de contado',
            },
            {
                icon: Package,
                color: 'text-foreground',
                name: 'Kit',
                text: 'teléfono + accesorios con ahorro',
            },
            {
                icon: X,
                color: 'text-muted-foreground',
                name: 'Quitar',
                text: 'la ✕ del resumen suelta la línea',
            },
            {
                icon: Gift,
                color: 'text-info',
                name: 'Regalo',
                text: 'accesorio gratis (no suma al total)',
            },
        ],
    };
}

export function HowItWorksModal({
    isOpen,
    onClose,
    content = PHONE_SELECT_HOW_IT_WORKS,
}: {
    isOpen: boolean;
    onClose: () => void;
    content?: HowItWorksContent;
}) {
    const zonesTitle = `Las ${String(content.zones.length)} zonas`;
    const stepsTitle = content.stepsTitle ?? 'Cómo armar la venta';
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={HOW_IT_WORKS_TITLE} size="xl">
            <div className="flex max-h-[70vh] flex-col gap-5 overflow-y-auto text-[13px]">
                <section aria-label={zonesTitle} className="flex flex-col gap-2">
                    <h3 className={SECTION}>{zonesTitle}</h3>
                    {content.sketch}
                    <ul className="flex flex-col gap-1">
                        {content.zones.map((zone) => (
                            <li key={zone.name} className="flex items-start gap-2">
                                <span
                                    aria-hidden
                                    className={cn(
                                        'mt-1 size-3 flex-none rounded-sm border-2',
                                        zone.swatch,
                                    )}
                                />
                                <span>
                                    <b>{zone.name}:</b> {zone.text}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section aria-label={stepsTitle} className="flex flex-col gap-2">
                    <h3 className={SECTION}>{stepsTitle}</h3>
                    <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {content.steps.map((step, index) => (
                            <li
                                key={step.title}
                                className="flex flex-col items-center gap-1.5 rounded-lg border border-border p-3 text-center"
                            >
                                <span className="flex size-6 items-center justify-center rounded-full bg-foreground text-[12px] font-semibold text-background">
                                    {index + 1}
                                </span>
                                <step.icon aria-hidden className="size-5" />
                                <span className="font-semibold">{step.title}</span>
                                <span className="text-[12px] text-muted-foreground">
                                    {step.text}
                                </span>
                            </li>
                        ))}
                    </ol>
                </section>

                <section aria-label={content.legendTitle} className="flex flex-col gap-2">
                    <h3 className={SECTION}>{content.legendTitle}</h3>
                    <ul className="grid gap-2 rounded-lg border border-border bg-muted/30 p-3 sm:grid-cols-2">
                        {content.legend.map((c) => (
                            <li key={c.name} className="flex items-center gap-2">
                                <c.icon aria-hidden className={cn('size-4 flex-none', c.color)} />
                                <span>
                                    <b className={c.color}>{c.name}:</b> {c.text}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
            <div className="flex justify-end pt-2">
                <Button type="button" onClick={onClose}>
                    Entendido
                </Button>
            </div>
        </Modal>
    );
}

/** Botón «?» que abre «¿Cómo funciona?»; se monta junto al título del paso. */
export function HowItWorksButton({ content }: { content?: HowItWorksContent }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                aria-label={HOW_IT_WORKS_TITLE}
                title={HOW_IT_WORKS_TITLE}
                onClick={() => {
                    setOpen(true);
                }}
                className="inline-flex size-7 flex-none items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <CircleHelp aria-hidden className="size-5" />
            </button>
            <HowItWorksModal
                isOpen={open}
                onClose={() => {
                    setOpen(false);
                }}
                {...(content !== undefined && { content })}
            />
        </>
    );
}
